import * as Sentry from "@sentry/react"
import * as API from "../api"
import { GUEST_LOGGED_IN, logUserAmplitudeEvent, setUserAsGuest } from "../common/amplitude-event-logging"
import Rmbx from "../util"
import { defaultErrorMsg } from "./index"
import { customFormToggleEditing } from "./form-view-actions"
import { eAuthenticationErrorTypes } from "../router/types"
import { setCookie } from "../common/ts-utils"
import { getFlagEnabled } from "../getFlagValue"
import { AUTH_EXPIRY } from "../common/constants"
import { autodeskLoginWithPopup, storeAutodeskClientConfig } from "../services/autodesk-identity"
import { cacheEmployeeToken, getCachedEmployeeToken } from "../common/employeeTokenCache"

/**
 * Persist an auth token in the appropriate storage for the current session.
 *
 * Mirrors the storage strategy used at login time: cookies when the
 * WA-7694-cookies-for-auth flag is active and the session is already
 * cookie-backed, Rmbx.store otherwise.
 *
 * When upgrading to cookie-backed storage (e.g. during company switching after
 * a session-only login), any token previously stored in sessionStorage is cleared
 * to prevent it from being discovered after logout and triggering a spurious
 * second login attempt.
 *
 * To gate the company-switcher token cache behind a feature flag, also
 * wrap the callers of getCachedEmployeeToken / cacheEmployeeToken with
 * `getFlagEnabled("your-flag-key")`.
 *
 * Parameters:
 * token (string): The plaintext Knox auth token to persist.
 * keepLoggedIn (boolean): Whether to persist beyond the current session
 *     (only meaningful for the initial login path).
 */
export const storeAuthToken = (token, keepLoggedIn = true) => {
    if (getFlagEnabled("WA-7694-cookies-for-auth") && keepLoggedIn) {
        setCookie("token", token, "/", AUTH_EXPIRY)
        setCookie("authorization_type", "Token", "/", AUTH_EXPIRY)
        // A session-only login (keepLoggedIn=false) stores the token in
        // sessionStorage. If the user then switches company, this function is
        // called with keepLoggedIn=true and the token moves to a cookie. The
        // original sessionStorage entry becomes orphaned: after logout clears
        // cookies + localStorage, setupSession re-adopts sessionStorage and
        // getAuthToken() finds the stale token, requiring a second logout to
        // clear it. Removing it here prevents that.
        window.sessionStorage.removeItem("token")
        window.sessionStorage.removeItem("authorization_type")
    } else {
        Rmbx.store.set("token", token)
        Rmbx.store.set("authorization_type", "Token")
    }
}

export function login_began() {
    return {
        type: "LOGIN_BEGAN",
    }
}

export function login_failed(errorMessage) {
    return {
        type: "LOGIN_FAILED",
        data: { errorMessage },
    }
}

export function login_successful(token) {
    return {
        type: "LOGIN_SUCCESSFUL",
        token,
    }
}

export function login_check_began() {
    return {
        type: "LOGIN_CHECK_BEGAN",
    }
}

export function login_check_reset() {
    return {
        type: "LOGIN_CHECK_RESET",
    }
}

export function login_check_failed(errorMessage) {
    return {
        type: "LOGIN_CHECK_FAILED",
        data: { errorMessage },
    }
}

export function login_check_successful(
    email,
    isOidcSsoEnabled,
    // can be removed with RN-1757-cico-worker-experience
    unverifiedUserRole,
    oidcUrl,
    scope,
    clientId,
    connectionName,
    connectionId,
    connectionLogo,
    onAssignedExceptionsList
) {
    return {
        type: "LOGIN_CHECK_SUCCESSFUL",
        data: {
            email,
            isOidcSsoEnabled,
            // can be removed with RN-1757-cico-worker-experience
            unverifiedUserRole,
            oidcUrl,
            scope,
            clientId,
            connectionName,
            connectionId,
            connectionLogo,
            onAssignedExceptionsList,
        },
    }
}

export function guest_login_check_successful(data) {
    return {
        type: "GUEST_LOGIN_CHECK_SUCCESSFUL",
        data: {
            email: data.email,
            formId: data.form_id,
            firstName: data.first_name,
            lastName: data.last_name,
            permissions: data.permissions,
            companyLogoUrl: data.company_logo_url,
            expiration: data.expiration,
        },
    }
}

export function autodesk_login_available(result) {
    return {
        type: "AUTODESK_LOGIN_AVAILABLE",
        data: {
            isAutodeskLoginAvailable: result.available || false,
            isAutodeskLoginOnly: result.only || false,
            autodeskClientId: result.client_id || null,
            autodeskEnv: result.env || null,
        },
    }
}

export function guest_login_check_failed(errorMessage) {
    return {
        type: "GUEST_LOGIN_CHECK_FAILED",
        data: { errorMessage },
    }
}

export function trial_account_deactivated() {
    return {
        type: "TRIAL_ACCOUNT_DEACTIVATED",
    }
}

// This sets a flag that lets us that the user was assigned a project
// after logging in or refreshing the page
export const initializedUser = () => ({
    type: "INITIALIZED",
})

export function login(data) {
    // if WA-7875-enforce-remember-me-setting flag is off or keep me logged in is checked, persist login
    const keepLoggedIn = !!data?.keepLoggedIn || !getFlagEnabled("WA-7875-enforce-remember-me-setting")
    return dispatch => {
        dispatch(login_began())
        return API.login(data)
            .then(result_data => {
                storeAuthToken(result_data[0].token, keepLoggedIn)
                cacheEmployeeToken(result_data[0].user.employee_id, result_data[0].token)
                dispatch(login_successful(result_data[0]))
                Sentry.configureScope(function (scope) {
                    scope.setUser({
                        email: result_data[0].user.email,
                        id: result_data[0].user.id,
                    })
                })
                Rmbx.util.history.push("/rhumbix/")
            })
            .catch(error => {
                const error_type = error.response.data?.error_type
                if (error_type === eAuthenticationErrorTypes.TRIAL_DISABLED) {
                    dispatch(trial_account_deactivated())
                    Rmbx.util.history.push("/rhumbix/trial/deactivated/")
                } else {
                    const errorMessage = error.response && error.response.status === 401 ? error.response.data.detail : defaultErrorMsg
                    dispatch(login_failed(errorMessage))
                }
            })
    }
}

export function loginCheck(data) {
    return dispatch => {
        dispatch(login_check_began())
        return API.loginCheck(data)
            .then(result => {
                dispatch(
                    login_check_successful(
                        result.email,
                        result.is_oidc_sso_enabled,
                        // can be removed with RN-1757-cico-worker-experience
                        result.user_role,
                        result.oidc_url,
                        result.scope,
                        result.client_id,
                        result.connection_name,
                        result.connection_id,
                        result.connection_logo,
                        result.on_assigned_exceptions_list
                    )
                )
                dispatch(
                    autodesk_login_available({
                        available: result.autodesk_login_available || false,
                        only: result.autodesk_login_only || false,
                        client_id: result.autodesk_client_id || null,
                        env: result.autodesk_env || null,
                    })
                )
            })
            .catch(error => {
                const errorMessage = error?.response?.data?.errors
                    ? typeof Object.values(error.response.data.errors)[0] === "string"
                        ? Object.values(error.response.data.errors)[0]
                        : Object.values(error.response.data.errors)[0][0]
                    : defaultErrorMsg
                dispatch(login_check_failed(errorMessage))
            })
    }
}

// Frontend routes for the Autodesk company-setup flow (WA-9395).
export const AUTODESK_SETUP_ROUTE = "/rhumbix/autodesk-setup"
export const AUTODESK_NO_COMPANY_ROUTE = "/rhumbix/autodesk-no-company"

/**
 * Decide where to send the user after a successful Autodesk login.
 *
 * Routes off the reliable `has_company` flag on the login response so the decision
 * does not depend on the separate (occasionally throttled) licensable-features call:
 *  - has_company -> the app;
 *  - employee-less -> the WA-9395 setup wizard, which fetches eligible contexts there
 *    and itself falls back to the "contact your Admin" page when there are none.
 * This keeps existing-company users off licensable-features entirely and prevents an
 * employee-less user from ever being dropped into the company-scoped app shell.
 *
 * @param {Object} loginResult - The login response payload (result[0]) carrying has_company.
 * @param {Function} dispatch - Redux dispatch, used to seed a minimal current_user.
 */
function routeAfterAutodeskLogin(loginResult, dispatch) {
    if (loginResult && loginResult.has_company) {
        Rmbx.util.history.push("/rhumbix/")
        return
    }
    // Employee-less Autodesk user: seed a minimal current_user so the logged-in shell
    // (sidebar with the user's email + Sign Out) renders without the company bootstrap.
    // The is_minimal flag lets get_current_user still fetch the real user once a company
    // exists (after provisioning). is_autodesk_user hides the Change Password menu item.
    dispatch({
        type: "UPDATE_CURRENT_USER",
        data: {
            // user_id is required — the Sidebar renders null without it.
            user_id: loginResult?.user?.id,
            email: loginResult?.user?.email ?? "",
            is_autodesk_user: true,
            is_minimal: true,
        },
    })
    Rmbx.util.history.push(AUTODESK_SETUP_ROUTE)
}

export function autodeskLogin({ email, autodeskClientId, autodeskEnv, keepLoggedIn }) {
    return async dispatch => {
        dispatch(login_began())
        try {
            const { accessToken, idToken } = await autodeskLoginWithPopup(autodeskClientId, autodeskEnv, email)
            const result = await API.autodeskLogin({ access_token: accessToken, id_token: idToken })
            const shouldPersist = !!keepLoggedIn || !getFlagEnabled("WA-7875-enforce-remember-me-setting")
            storeAuthToken(result[0].token, shouldPersist)
            cacheEmployeeToken(result[0].user.employee_id, result[0].token)
            storeAutodeskClientConfig(autodeskClientId, autodeskEnv)
            dispatch(login_successful(result[0]))
            Sentry.configureScope(function (scope) {
                scope.setUser({
                    email: result[0].user.email,
                    id: result[0].user.id,
                })
            })
            routeAfterAutodeskLogin(result[0], dispatch)
        } catch (e) {
            Sentry.captureException(e)
            dispatch(login_failed("Autodesk authentication failed. Please try again."))
        }
    }
}

export function guestLogin(data) {
    return dispatch => {
        return API.guestLogin(data)
            .then(result => {
                dispatch(guest_login_check_successful(result))
                Rmbx.store.set("guest_email", result.email)
                Rmbx.store.set("guest_form_id", result.form_id)
                Rmbx.store.set("guest_company_logo", result.company_logo)
                Rmbx.store.set("guest_permissions", result?.permissions)
                Rmbx.store.set("guest_signed_key", data.signed_key)
                Sentry.configureScope(function (scope) {
                    scope.setUser({
                        email: result.email,
                        id: 0,
                    })
                })
                Rmbx.util.history.push(`/rhumbix/guest_access/${data.signed_key}/`)
                const eventInfo = {
                    form_id: result.form_id,
                    permissions: result.permissions,
                }
                setUserAsGuest(true)
                logUserAmplitudeEvent(GUEST_LOGGED_IN, eventInfo)
            })
            .catch(error => {
                const errorMessage = error.response && error.response.data && error.response.data.errors ? Object.values(error.response.data.errors)[0] : defaultErrorMsg
                dispatch(guest_login_check_failed(errorMessage))
                dispatch(customFormToggleEditing(false))
                Rmbx.util.history.push(`/rhumbix/guest_login/${data.signed_key}/`)
            })
    }
}

export function guestAccessExpired(signedKey) {
    return dispatch => {
        dispatch(guest_login_check_failed("Your invitation has expired"))
        Rmbx.util.history.push(`/rhumbix/guest_login/${signedKey}/`)
    }
}

/**
 * Switch the active session to a different employee/company context.
 *
 * Uses a localStorage-cached Knox token for the target employee when one is
 * available, avoiding a server round-trip.  Falls back to calling
 * POST /api/v4/user_employees/{id}/switch_company/ when no cache entry
 * exists, then caches the resulting token for future switches.
 *
 * If the cached token has expired the server will return 401 on the
 * subsequent getCurrentUser() call, which triggers the normal logout flow.
 * A future improvement can intercept that 401 and retry with a fresh token.
 *
 * Parameters:
 * employeeId (number): PK of the employee to switch to.
 */
export function switchCompany(employeeId) {
    return async dispatch => {
        try {
            let token = getCachedEmployeeToken(employeeId)

            if (!token) {
                const result = await API.switchCompanyApi(employeeId)
                token = result[0].token
                cacheEmployeeToken(employeeId, token)
            }

            // Use the same storage strategy as the current session.
            // keepLoggedIn=true so cookie-backed sessions stay cookie-backed.
            storeAuthToken(token, true)

            // Hard reload required: many Redux slices cache company-scoped data
            // (employees, projects, cost codes, etc.) that is invalid after a switch.
            window.location.assign("/rhumbix/")
        } catch {
            dispatch(login_failed("Failed to switch company. Please try again."))
        }
    }
}
